import React from 'react';

import { CallProvider } from '../../shared/contexts/CallProvider';
import { MediaDeviceProvider } from '../../shared/contexts/MediaDeviceProvider';
import { ParticipantsProvider } from '../../shared/contexts/ParticipantsProvider';
import { ScreenShareProvider } from '../../shared/contexts/ScreenShareProvider';
import { TracksProvider } from '../../shared/contexts/TracksProvider';
import { UIStateProvider } from '../../shared/contexts/UIStateProvider';
import { WaitingRoomProvider } from '../../shared/contexts/WaitingRoomProvider';
import { TranscriptionProvider } from '../../shared/contexts/TranscriptionProvider';
import { ChatProvider } from '../../shared/contexts/ChatProvider';
import { RecordingProvider } from '../../shared/contexts/RecordingProvider';
import { PipProvider } from '../../shared/contexts/PipProvider';
import { Call } from '../../Call/Call/Call';
import { Tray } from '../../Call/Tray';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { Route, Switch, useLocation } from 'react-router-dom';
import { ICallParams } from '../../types';
import { theme } from '../../../../../common/styles/theme';
import { Paper } from '../../../../../common/components/Paper/Paper';
import { ICallAppProps } from './types';
import { HeaderHOC } from '../../../ToolbarPanel/Header/hocs/HeaderHOC';
import { UrlUtil } from '../../../../../common/utils/url-util';
import { PreviewHOC } from '../../Preview/hocs/PreviewHOC';
import { Text } from '../../../../../common/components/Text/Text';
import { ButtonLogin } from '../../../install/Login/components/ButtonLogin/ButtonLogin';
import { LoginConsent } from '../../../install/Login/components/LoginConsent/LoginConsent';
import { NewCallPanel } from '../../NewCallPanel/components/NewCallPanel';
import { MyLibRecordingsHOC } from '../../CallRecordingsPage/MyLibRecordings/hocs/MyLibRecordingsHOC';
import { DeviceSelect } from '../../shared/components/DeviceSelect/DeviceSelect';
import { Modal } from '../../../../../common/components/Modal/Modal';
import { CallNotice } from '../../CallNotice/components/CallNotice';
import { Footer } from '../../../../../common/components/Footer/Footer';
export function CallApp({
    isActive,
    roomId,
    token,
    lang,
    multilingual,
    isTeacher,
    isAnonym,
    isLangMulti,
    isNeedInstallSelectLangs,
    notice,
    recordingsReady,
    hasRecordings,
    saveLevel,
    onLoad,
    onCreate,
    onJoin,
    onRestore,
    onLeave,
    onLogin,
    onChangeMultilingual,
    checkLangs,
    onCloseNotice,
}: ICallAppProps) {

    const location = useLocation();
    let subscribeToTracksAutomatically = true;
    const [isStart, setStart] = React.useState(true);
    const [authResolved, setAuthResolved] = React.useState(false);
    const [showSettings, setShowSettings] = React.useState(false);
    const [isCreating, setCreating] = React.useState(false);
    // const [isTeacher, setTeacher] = React.useState(false);
    // const [isCreator, setCreator] = React.useState(false);
    // const [roomId, setRoomId] = React.useState('');
    // const [token, setToken] = React.useState('');
    // const [_lang, setLang] = React.useState(lang);

    const params: ICallParams = UrlUtil.getRouteCall();
    const {
        roomId: roomParam,
        // isTeacher: teacher,
    } = params || {};

    React.useEffect(() => {
        if (isAnonym !== false) return;

        if (isActive) {
            setStart(false);
        } else {
            setStart(true);
        }
    }, [isActive, isAnonym]);

    React.useEffect(() => {
        if (roomParam && isAnonym === false) {
            onJoinHandle();
        }
    }, [isAnonym]);

    // Site reopened with a call left in localStorage (no room in the URL): check it before entering
    // Daily — entering would reopen a call that may already be finished and processed.
    const [restoreChecked, setRestoreChecked] = React.useState(() => !(isActive && roomId && !roomParam));
    React.useEffect(() => {
        if (restoreChecked || isAnonym !== false) return;
        onRestore(roomId!).finally(() => setRestoreChecked(true));
    }, [isAnonym]);

    const createRoom = async () => {
        if (!lang || isCreating) return;
        setCreating(true);
        try {
            await onCreate();
        } catch (e) {
            console.error(e);
        } finally {
            setCreating(false);
        }
    };

    const onJoinHandle = () => {
        onJoin(roomParam || roomId);
    };

    const onChangeMultilingualHandle = () => {
        onChangeMultilingual(!multilingual);
    };

    // CallAppHOC never remounts, so re-run onLoad per route entry instead. onLoad is idempotent
    // (guarded by an existing user).
    React.useEffect(() => {
        if (!UrlUtil.needsAuthBootstrap()) return;
        onLoad().finally(() => setAuthResolved(true));
    }, [location.pathname]);

    React.useEffect(() => {
        if (isActive && !roomId && !roomParam) onLeave();

        return () => {
            onLeave();
        }
    }, []);

    const wasInCallRef = React.useRef(false);
    React.useEffect(() => {
        if (isAnonym !== false) return;
        if (!isNeedInstallSelectLangs) return;
        if (roomId || UrlUtil.getRouteCall()) {
            wasInCallRef.current = true;
            return;
        }
        if (!wasInCallRef.current) return;
        wasInCallRef.current = false;
        checkLangs();
    }, [isAnonym, roomId, location.pathname, isNeedInstallSelectLangs]);

    // if the recordings never load (request failed), stop waiting and show the form next to the list's spinner
    const [recordingsWaitOver, setRecordingsWaitOver] = React.useState(false);
    React.useEffect(() => {
        // counted from sign-in, not mount: the auth bootstrap alone can take seconds
        if (isAnonym !== false || hasRecordings !== undefined) return;
        const timer = setTimeout(() => setRecordingsWaitOver(true), 10000);
        return () => clearTimeout(timer);
    }, [isAnonym, hasRecordings]);

    // the new-call form, or a note about the last call in its place until dismissed
    const newCallColumn = notice
        ? <CallNotice
            notice={notice}
            recordingsReady={recordingsReady}
            onClose={onCloseNotice}
        />
        : <NewCallPanel
            lang={lang}
            isCreating={isCreating}
            hasLastMeeting={!!roomId}
            onJoinLast={onJoinHandle}
            onCreate={createRoom}
            onOpenSettings={() => setShowSettings(true)}
        />;

    return (
        <UIStateProvider
            asides={[]}
            modals={[]}
            customTrayComponent={<Tray />}
        >
            <CallProvider
                domain={'langmagic'}
                room={isActive && isAnonym === false && restoreChecked && roomId}
                token={token}
                initialSaveLevel={saveLevel}
                subscribeToTracksAutomatically={subscribeToTracksAutomatically}
            >
                <ParticipantsProvider>
                    <TracksProvider>
                        <MediaDeviceProvider>
                            <WaitingRoomProvider>
                                <ScreenShareProvider>
                                    <RecordingProvider>
                                        <ChatProvider>
                                            <TranscriptionProvider>
                                                <PipProvider>
                                                    <Switch>
                                                        <Route exact path={[
                                                            '/call/:roomId/:isTeacher?',
                                                            '/',
                                                        ]}>
                                                            <Stack
                                                                sx={{
                                                                    width: 1,
                                                                    height: 1,
                                                                    // the header stays put: the page body below it is what scrolls
                                                                    overflow: 'hidden',
                                                                    fontSize: `1rem`,
                                                                    background: theme.palette.grey[100],

                                                                    '.footer': {
                                                                        transform: `translateY(${theme.spacing(-1)})`,
                                                                    },
                                                                }}
                                                            >
                                                                {/* no header in a call: it takes the whole screen; on a call link wait until we know it won't be one */}
                                                                {(roomParam
                                                                    ? isAnonym === true || (isAnonym === false && isStart && !!notice)
                                                                    : isAnonym !== false || isStart
                                                                ) &&
                                                                    <HeaderHOC />
                                                                }

                                                                {/* Guests on `/` normally get the server's static landing (backend/html/landing.html) */}
                                                                {isAnonym !== false &&
                                                                    <Stack
                                                                        sx={{
                                                                            width: 1,
                                                                            flex: 1,
                                                                            minHeight: 0,
                                                                            overflowY: 'auto',
                                                                        }}
                                                                    >
                                                                        <Stack
                                                                            gap={theme.spacing(3)}
                                                                            justifyContent={'center'}
                                                                            alignItems={'center'}
                                                                            sx={{
                                                                                width: 1,
                                                                                flex: 1,
                                                                            }}
                                                                        >
                                                                            {/* isAnonym is undefined both mid-bootstrap and after it gave up on a session; only the latter offers a login. */}
                                                                            {isAnonym || authResolved
                                                                                ? <>
                                                                                    <Text
                                                                                        sx={{
                                                                                            fontSize: theme.typography.subtitle2,
                                                                                            fontWeight: 300,
                                                                                        }}
                                                                                    >
                                                                                        Please login to use LangMagic
                                                                                    </Text>
                                                                                    <ButtonLogin
                                                                                        onLogin={onLogin}
                                                                                    />
                                                                                    <LoginConsent />
                                                                                </>
                                                                                : <CircularProgress
                                                                                    size={theme.spacing(3)}
                                                                                />
                                                                            }
                                                                        </Stack>
                                                                        <Footer />
                                                                    </Stack>
                                                                }

                                                                {isAnonym === false &&
                                                                    <Stack
                                                                        className={'page-body'} // the header's logo scrolls it up
                                                                        sx={{
                                                                            width: 1,
                                                                            // the call scrolls inside itself, the home page scrolls here, under the header
                                                                            ...(isStart ? { flex: 1, minHeight: 0, overflowY: 'auto' } : { height: 1 }),
                                                                        }}
                                                                    >
                                                                        {isStart && <>
                                                                            {hasRecordings === undefined && !recordingsWaitOver
                                                                                // which layout to show isn't known yet: don't flash one and jump to the other
                                                                                ? <Stack
                                                                                    justifyContent={'center'}
                                                                                    alignItems={'center'}
                                                                                    sx={{
                                                                                        flex: '1 0 auto',
                                                                                        width: 1,
                                                                                    }}
                                                                                >
                                                                                    <CircularProgress
                                                                                        size={theme.spacing(3)}
                                                                                    />
                                                                                </Stack>
                                                                            : hasRecordings !== false
                                                                                ? <Stack
                                                                                    direction={'row'}
                                                                                    gap={2}
                                                                                    justifyContent={'center'}
                                                                                    alignItems={'flex-start'}
                                                                                    sx={{
                                                                                        flex: '1 0 auto',
                                                                                        width: 1,
                                                                                        p: theme.spacing(2),

                                                                                        [theme.breakpoints.down('md')]: {
                                                                                            flexDirection: 'column',
                                                                                            alignItems: 'stretch',
                                                                                            p: theme.spacing(1),
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <Paper
                                                                                        sx={{
                                                                                            flex: 1,
                                                                                            minWidth: 0,
                                                                                            maxWidth: '1200px',
                                                                                            px: 3,

                                                                                            [theme.breakpoints.down('md')]: {
                                                                                                flex: '0 0 auto',
                                                                                                maxWidth: 'none',
                                                                                                px: 1,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <MyLibRecordingsHOC />
                                                                                    </Paper>

                                                                                    <Box
                                                                                        sx={{
                                                                                            width: '400px',
                                                                                            flexShrink: 0,
                                                                                            position: 'sticky',
                                                                                            top: theme.spacing(2),

                                                                                            [theme.breakpoints.down('lg')]: {
                                                                                                width: '280px',
                                                                                            },

                                                                                            [theme.breakpoints.down('md')]: {
                                                                                                width: 1,
                                                                                                position: 'static',
                                                                                                order: -1,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        {newCallColumn}
                                                                                    </Box>
                                                                                </Stack>
                                                                                // no recordings yet: the form centered, a short how-to to its right (below it on mobile)
                                                                                : <Box
                                                                                    sx={{
                                                                                        flex: '1 0 auto',
                                                                                        width: 1,
                                                                                        p: theme.spacing(2),
                                                                                        display: 'grid',
                                                                                        gridTemplateColumns: '1fr minmax(0, 400px) 1fr',
                                                                                        alignItems: 'center',
                                                                                        alignContent: 'center',
                                                                                        gap: 3,

                                                                                        [theme.breakpoints.down('md')]: {
                                                                                            gridTemplateColumns: 'minmax(0, 400px)',
                                                                                            justifyContent: 'center',
                                                                                            gap: 2,
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <Box
                                                                                        sx={{
                                                                                            gridColumn: 2,

                                                                                            [theme.breakpoints.down('md')]: {
                                                                                                gridColumn: 1,
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        {newCallColumn}
                                                                                    </Box>
                                                                                    {!notice &&
                                                                                        // a tip styled apart from the page: it's gone once there are recordings
                                                                                        <Stack
                                                                                            direction={'row'}
                                                                                            alignItems={'flex-start'}
                                                                                            gap={1}
                                                                                            sx={{
                                                                                                maxWidth: '320px',
                                                                                                p: theme.spacing(1.5, 2),
                                                                                                borderRadius: theme.spacing(.75),
                                                                                                fontSize: theme.typography.subtitle2,
                                                                                                color: theme.palette.grey[400],
                                                                                                'b': {
                                                                                                    color: theme.palette.grey[600],
                                                                                                },
                                                                                            }}
                                                                                        >
                                                                                            <Box
                                                                                                sx={{
                                                                                                    flexShrink: 0,
                                                                                                    fontSize: '28px',
                                                                                                    lineHeight: 1,
                                                                                                    transform: 'translateY(-4px)',
                                                                                                }}
                                                                                            >
                                                                                                💡
                                                                                            </Box>
                                                                                            <Stack
                                                                                                gap={1}
                                                                                            >
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        fontWeight: 600,
                                                                                                        color: theme.palette.grey[600],
                                                                                                    }}
                                                                                                >
                                                                                                    {isTeacher ? 'Start your first lesson' : 'Start your first call'}
                                                                                                </Box>
                                                                                                {isTeacher
                                                                                                    ? <Box>
                                                                                                        Pick the language you’re teaching, then click <b>New meeting</b> and send the link to your student. When the call ends, the recording, transcript and exercises appear here.
                                                                                                    </Box>
                                                                                                    : <Box>
                                                                                                        Pick the language you’re learning, then click <b>New meeting</b> and send the link to the person you want to talk to. When the call ends, the recording, transcript and exercises appear here.
                                                                                                    </Box>
                                                                                                }
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        mt: 1,
                                                                                                    }}
                                                                                                >
                                                                                                    Check your camera and microphone in <b>Input/Output settings</b>.
                                                                                                </Box>
                                                                                            </Stack>
                                                                                        </Stack>
                                                                                    }
                                                                                </Box>
                                                                            }

                                                                            {showSettings &&
                                                                                <Modal
                                                                                    title={'Select your device'}
                                                                                    open={showSettings}
                                                                                    onClose={() => setShowSettings(false)}
                                                                                    PaperProps={{
                                                                                        sx: {
                                                                                            width: `50vw !important`,
                                                                                            minWidth: `50vw !important`,
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <DeviceSelect showPreview />
                                                                                </Modal>
                                                                            }

                                                                            <Footer />
                                                                        </>}

                                                                        {!isStart &&
                                                                            <Call key={`call-${isActive}`} />
                                                                        }
                                                                    </Stack>
                                                                }

                                                            </Stack>
                                                        </Route>

                                                        <Route>
                                                            {isActive && roomId &&
                                                                <PreviewHOC />
                                                            }
                                                        </Route>
                                                    </Switch>

                                                </PipProvider>
                                            </TranscriptionProvider>
                                        </ChatProvider>
                                    </RecordingProvider>
                                </ScreenShareProvider>
                            </WaitingRoomProvider>
                        </MediaDeviceProvider>
                    </TracksProvider>
                </ParticipantsProvider>
            </CallProvider>
        </UIStateProvider>
    );
}
